import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  ChevronRight,
  ExternalLink,
  Search,
} from 'lucide-react'
import { glossaryEntries, type GlossaryEntry } from '@/data/glossary'
import { useSEO } from '@/hooks/useSEO'
import { MarketingHeader } from '@/components/marketing/MarketingHeader'
import { MarketingFooter } from '@/components/marketing/MarketingFooter'

function TermCard({ entry }: { entry: GlossaryEntry }) {
  return (
    <Link
      to={`/glossary/${entry.slug}`}
      className="group flex flex-col rounded-2xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-50 transition-all duration-300 overflow-hidden"
    >
      <div className="p-6 sm:p-8 flex flex-col flex-1">
        <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors leading-snug">
          {entry.term}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
          {entry.shortDefinition}
        </p>
        <div className="flex items-center justify-end pt-4 border-t border-gray-100">
          <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
            Read more
            <ChevronRight size={16} />
          </span>
        </div>
      </div>
    </Link>
  )
}

export function GlossaryList() {
  useSEO({
    title: 'Glossary',
    description:
      'Key terms and concepts in AI-powered document extraction, intelligent document processing, OCR, LLM extraction, RAG, and more.',
    canonical: 'https://documentiq.algoscale.com/glossary',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'DefinedTermSet',
        name: 'DocumentIQ Glossary',
        description: 'Key terms in AI document extraction and intelligent document processing.',
        url: 'https://documentiq.algoscale.com/glossary',
        hasDefinedTerm: glossaryEntries.map((e) => ({
          '@type': 'DefinedTerm',
          name: e.term,
          description: e.shortDefinition,
          url: `https://documentiq.algoscale.com/glossary/${e.slug}`,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://documentiq.algoscale.com' },
          { '@type': 'ListItem', position: 2, name: 'Glossary', item: 'https://documentiq.algoscale.com/glossary' },
        ],
      },
    ],
  })

  const sorted = useMemo(
    () => [...glossaryEntries].sort((a, b) => a.term.localeCompare(b.term)),
    []
  )

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium mb-6">
            <BookOpen size={14} />
            Knowledge Base
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 leading-tight">
            Document AI Glossary
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Key terms and concepts in intelligent document processing, AI extraction,
            and document automation — explained in plain language.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sorted.map((entry) => (
              <TermCard key={entry.slug} entry={entry} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-gray-50 border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">More from Algoscale</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: 'AI Consulting Services', url: 'https://algoscale.com/ai-consulting-service/' },
              { title: 'Generative AI Services', url: 'https://algoscale.com/generative-ai-services/' },
              { title: 'Data Engineering', url: 'https://algoscale.com/data-engineering-services/' },
              { title: 'Algoscale Blog', url: 'https://algoscale.com/blog/' },
            ].map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:border-blue-200 hover:text-blue-600 transition-colors"
              >
                {link.title} &rarr;
              </a>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}

export function GlossaryDetail() {
  const { slug } = useParams<{ slug: string }>()
  const entry = useMemo(
    () => glossaryEntries.find((e) => e.slug === slug),
    [slug]
  )

  const relatedEntries = useMemo(() => {
    if (!entry) return []
    return entry.relatedTerms
      .map((s) => glossaryEntries.find((e) => e.slug === s))
      .filter(Boolean) as GlossaryEntry[]
  }, [entry])

  const isExternal = (url: string) => url.startsWith('http')

  useSEO({
    title: entry?.term || 'Glossary Term',
    description: entry?.shortDefinition || 'DocumentIQ glossary term definition.',
    canonical: entry ? `https://documentiq.algoscale.com/glossary/${entry.slug}` : undefined,
    ogType: 'article',
    ogImage: entry ? `/og/glossary/${entry.slug}.png` : undefined,
    jsonLd: entry
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'DefinedTerm',
            name: entry.term,
            description: entry.shortDefinition,
            url: `https://documentiq.algoscale.com/glossary/${entry.slug}`,
            inDefinedTermSet: {
              '@type': 'DefinedTermSet',
              name: 'DocumentIQ Glossary',
              url: 'https://documentiq.algoscale.com/glossary',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Glossary',
                item: 'https://documentiq.algoscale.com/glossary',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: entry.term,
                item: `https://documentiq.algoscale.com/glossary/${entry.slug}`,
              },
            ],
          },
        ]
      : undefined,
  })

  if (!entry) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Term not found.</p>
          <Link to="/glossary" className="text-blue-600 font-medium hover:underline">
            View all terms
          </Link>
        </div>
      </div>
    )
  }

  const paragraphs = entry.definition.split('\n\n')

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <MarketingHeader />

      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/glossary" className="hover:text-gray-900 transition-colors flex items-center gap-1">
              <ArrowLeft size={14} /> Glossary
            </Link>
            <span>/</span>
            <span className="text-gray-900 font-medium truncate">{entry.term}</span>
          </div>
        </div>
      </div>

      <article className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-4">
              <BookOpen size={12} />
              Glossary
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 leading-tight">
              {entry.term}
            </h1>
          </div>

          <div className="prose prose-gray prose-headings:font-semibold prose-headings:tracking-tight prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline max-w-none">
            {paragraphs.map((para, i) => (
              <p key={i} className="text-gray-700 leading-relaxed mb-5">
                {para}
              </p>
            ))}
          </div>

          {relatedEntries.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Related Terms</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relatedEntries.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/glossary/${related.slug}`}
                    className="group flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 bg-white hover:border-blue-200 hover:shadow-sm transition-all"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                        {related.term}
                      </p>
                    </div>
                    <ChevronRight size={16} className="text-gray-400 group-hover:text-blue-500 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {entry.relatedLinks.length > 0 && (
            <div className="mt-10 pt-8 border-t border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Related Resources</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {entry.relatedLinks.map((link) =>
                  isExternal(link.url) ? (
                    <a
                      key={link.url}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 bg-white hover:border-blue-200 hover:text-blue-600 transition-colors text-sm text-gray-700"
                    >
                      <span className="flex-1">{link.title}</span>
                      <ExternalLink size={14} className="text-gray-400 flex-shrink-0" />
                    </a>
                  ) : (
                    <Link
                      key={link.url}
                      to={link.url}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 bg-white hover:border-blue-200 hover:text-blue-600 transition-colors text-sm text-gray-700"
                    >
                      <span className="flex-1">{link.title}</span>
                      <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />
                    </Link>
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </article>

      <section className="py-12 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">
            See these concepts in action
          </h2>
          <p className="text-blue-100 mb-6 text-sm">
            DocumentIQ turns documents into structured data using the AI techniques described above.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-blue-600 font-medium hover:bg-blue-50 transition-colors shadow-md text-sm"
          >
            Start for Free <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <section className="border-t border-gray-200 bg-gray-50 py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-sm font-semibold text-gray-500 mb-4">More from Algoscale</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { title: 'AI Consulting', url: 'https://algoscale.com/ai-consulting-service/' },
              { title: 'Generative AI', url: 'https://algoscale.com/generative-ai-services/' },
              { title: 'Data Engineering', url: 'https://algoscale.com/data-engineering-services/' },
            ].map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:border-blue-200 hover:text-blue-600 transition-colors"
              >
                {link.title} &rarr;
              </a>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
